import type { Settings } from "@cuekards/core";
import type { DeliveryRequest, DeliveryResult } from "./delivery.js";

/**
 * The envelope the desktop app's renderer-to-main contract travels in between
 * the page and the core worker.
 *
 * The contract itself — every channel name in `@cuekards/core/ipc` and every
 * method on `CuekardsApi` — is unchanged, and that is the point. Electron gives a
 * renderer three shapes: `invoke` (a call with an answer), `send` (a call with
 * none) and `on` (a subscription to something main pushes). This module is those
 * three shapes over `postMessage`, and nothing else:
 *
 * - `invoke` becomes {@link ContractRequest} answered by {@link ContractResponse},
 *   correlated by an id, because a port has no reply channel of its own;
 * - `send` becomes {@link ContractSend}, which is never answered;
 * - a subscription becomes {@link ContractBroadcast}, pushed unprompted.
 *
 * Because it is the same contract, the shared components in `@cuekards/ui` compile
 * against it unmodified. Anything that would need a channel added here should be
 * suspected of being a change to the contract in disguise.
 *
 * The two settings messages are the one thing here that is *not* the contract,
 * and they are deliberately separate rather than smuggled in as channels: the
 * desktop has nothing corresponding, because there main writes its own file. See
 * {@link SettingsSnapshot}.
 */

/** `invoke`: a call the page expects an answer to. `args` carries the method's
 * arguments positionally, exactly as `ipcRenderer.invoke(channel, ...args)`. */
export interface ContractRequest {
  kind: "request";
  id: number;
  channel: string;
  args: unknown[];
}

/** The answer to one {@link ContractRequest}. A rejected call carries a message
 * rather than an error object, because an `Error` does not survive structured
 * cloning with anything a caller could act on. */
export type ContractResponse =
  | { kind: "response"; id: number; ok: true; value: unknown }
  | { kind: "response"; id: number; ok: false; message: string };

/** `send`: a call with no answer. Unhandled channels are dropped silently, which
 * is what `ipcMain` does with a send nobody listens for. */
export interface ContractSend {
  kind: "send";
  channel: string;
  args: unknown[];
}

/** What a subscription receives. One payload rather than an argument list,
 * because `webContents.send` is called with exactly one on every broadcast
 * channel and every `on…` method on the contract takes exactly one. */
export interface ContractBroadcast {
  kind: "broadcast";
  channel: string;
  payload: unknown;
}

/**
 * The stored settings blob, posted in by the page at worker startup.
 *
 * The page owns persistence because the worker cannot: the settings store's
 * accessors are synchronous and are read from inside controllers with no `await`
 * to give, and the browser's only synchronous storage is window-scoped and
 * therefore unreachable from a worker. So the page reads the blob, posts it in,
 * and the worker caches it — see `PageBackedSettingsBackend`.
 *
 * Ordering is load-bearing and it is free: a worker's messages are queued until
 * its script has finished evaluating, and a port delivers in order. As long as
 * the page posts this first, it is handled before any request that would read a
 * setting.
 */
export interface SettingsSnapshot {
  kind: "settings-snapshot";
  settings: Settings;
}

/** The other half: every write the worker makes, posted back out for the page to
 * store. Whole-blob rather than per-key, because that is what the backend
 * interface is. */
export interface SettingsWrite {
  kind: "settings-write";
  settings: Settings;
}

/**
 * The finished take, offered to the page to put somewhere.
 *
 * The second thing on this port that is not the contract, and it travels the
 * *other* way from everything else: the worker is asking, and the page is
 * answering. That is not a stylistic choice. The recording engine and the
 * session's bytes live in the worker because storage there is synchronous, and
 * both ways of getting a file out of a browser — the save dialog and a plain
 * download — are window APIs. So the one moment the two have to be joined is at
 * delivery, and the answer has to be awaited, because the engine discards a
 * session as soon as it is told the bytes are safe.
 *
 * Correlated by an id like a request, for the same reason: a port has no reply
 * channel of its own.
 */
export interface DeliverTakeRequest {
  kind: "deliver-take";
  id: number;
  request: DeliveryRequest;
}

/** The page's answer to one {@link DeliverTakeRequest}. */
export interface DeliverTakeResponse {
  kind: "deliver-take-result";
  id: number;
  result: DeliveryResult;
}

/** Everything the page sends. */
export type PageMessage =
  | ContractRequest
  | ContractSend
  | SettingsSnapshot
  | DeliverTakeResponse;

/** Everything the core worker sends. */
export type CoreMessage =
  | ContractResponse
  | ContractBroadcast
  | SettingsWrite
  | DeliverTakeRequest;

/**
 * The bit of a port both halves use. Structural rather than a named platform
 * type on purpose: a `Worker`, a `DedicatedWorkerGlobalScope`, a `MessagePort`
 * and Node's own `MessagePort` all satisfy it, and the last of those is what
 * lets the whole application be driven through the contract in a test with no
 * browser at all.
 */
export interface MessagePortLike {
  postMessage(message: unknown): void;
  addEventListener(
    type: "message",
    listener: (event: { data: unknown }) => void,
  ): void;
  removeEventListener(
    type: "message",
    listener: (event: { data: unknown }) => void,
  ): void;
  /** Present on ports, absent on workers — a port delivers nothing until it is
   * started when listeners were attached with `addEventListener`. */
  start?(): void;
}

/** The `kind` of an arriving message, or undefined for anything that is not one
 * of ours. Both halves share a port with the other's traffic, so each filters
 * rather than assuming everything that arrives is addressed to it. */
export function messageKind(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const kind = (data as { kind?: unknown }).kind;
  return typeof kind === "string" ? kind : undefined;
}

/**
 * Listen to one side of a tagged conversation on a port, typed.
 *
 * Every private channel in this package is the same shape — a union tagged by
 * `kind`, each side sending its own and receiving the other's — so this is
 * written once and instantiated per conversation rather than being four
 * near-identical listeners. It lives here beside {@link messageKind} because
 * that is what it is built out of, and because every port in the package
 * carries more than one of these: the recognition worker's own scope has the
 * speech link's handover, eight audio frames a second and the model download's
 * commands on it, so filtering by kind is not defensive tidiness but the only
 * way to read one conversation off a port carrying three.
 *
 * Unknown messages are dropped: a dropped message is cheaper than a thrown one
 * on the frame path.
 *
 * Returns the unsubscribe.
 */
export function onLinkMessage<Received extends { kind: string }>(
  link: MessagePortLike,
  kinds: readonly Received["kind"][],
  handle: (message: Received) => void,
): () => void {
  const known = new Set<string>(kinds);
  const receive = (event: { data: unknown }): void => {
    const kind = messageKind(event.data);
    if (kind === undefined || !known.has(kind)) return;
    handle(event.data as Received);
  };
  link.addEventListener("message", receive);
  link.start?.();
  return () => link.removeEventListener("message", receive);
}
